// 월간/주간 성과보고서

import React, { useEffect, useMemo, useState } from 'react'
import { FileText } from 'lucide-react'
import Button from '@/components/ui/Button'
import Chart from '@/components/data/Chart'
import BusinessSelect from '@/components/ui/BusinessSelect'
import DateRangePicker from '@/components/ui/DateRangePicker'
import { formatDateForAPI, getMonthRange, getWeekNumber } from '@/components/ui/DateRangePicker/utils'
import { designTokens } from '@/utils/design-tokens'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchTaskSummaryMonth } from '@/store/taskSummarySlice'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError } from '@/utils/api-error-handler'
import { setCurrentAsOf } from '@/api/asOfContext'
import type { SnapshotItemResponse } from '@/api/generated/models/SnapshotItemResponse'
import { downloadPeriodReport, type ReportFormat } from './downloadPeriodReport'
import BusinessDepartmentSummary from './components/BusinessDepartmentSummary'
import PeriodSummaryTable from './components/PeriodSummaryTable'
import TaskListSection from './components/TaskListSection'
import SnapshotSelect from './components/SnapshotSelect'
import SummaryListPanels, { type SummaryListPanelState, type SummaryListPanelType } from './components/SummaryListPanels'
import { usePersistedBusinessFilter } from '@/hooks/usePersistedBusinessFilter'
import { useShowTasks } from '@/hooks/useShowTasks'

/** 보고서 주기 — 주간/월간. 필터 차이만 있고 화면 내용은 동일하다. */
export type ReportPeriod = 'weekly' | 'monthly'

interface PeriodReportDashboardProps {
  period: ReportPeriod
}

/** 스냅샷의 기본 조회 범위 — period_start/end 우선, 없으면 as_of(YYYY-MM)에서 월 범위 도출 */
const getSnapshotRange = (snapshot: SnapshotItemResponse): [Date | null, Date | null] => {
  if (snapshot.period_start && snapshot.period_end) {
    return [new Date(`${snapshot.period_start}T00:00:00`), new Date(`${snapshot.period_end}T00:00:00`)]
  }
  const m = /^(\d{4})-(\d{2})$/.exec(snapshot.as_of)
  if (m) {
    return getMonthRange(new Date(Number(m[1]), Number(m[2]) - 1, 1))
  }
  return [null, null]
}

// 주간/월간 성과보고서 공용 화면 — period로 필터/타이틀만 분기 (상단 영역만 구현, 그 아래 내용은 추후)
const PeriodReportDashboard: React.FC<PeriodReportDashboardProps> = ({ period }) => {
  const dispatch = useAppDispatch()
  const { showError } = useAlert()
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)
  const { monthSummary: taskSummaryMonth, isLoading: isSummaryLoading } = useAppSelector(state => state.taskSummary)

  // 요약 카드 클릭 → 항목별 목록 사이드패널
  const [summaryListPanel, setSummaryListPanel] = useState<SummaryListPanelState | null>(null)

  // 사업 필터
  const {
    businessId: selectedBusinessId,
    businessName: selectedBusinessName,
    setBusiness: saveSelectedBusiness,
  } = usePersistedBusinessFilter()
  const handleBusinessSelect = (businessId: string, businessName: string) => {
    saveSelectedBusiness(businessId, businessName)
  }

  // 섹션 타이틀 접두어 — 사업 미선택('전체' 옵션은 id/name이 빈 문자열)이면 '전체', 선택 시 사업명
  const sectionTitlePrefix = selectedBusinessId && selectedBusinessName ? selectedBusinessName : '전체'

  // 목록 사이드패널에 넘길 사업 필터 (미선택이면 전체 조회)
  const businessFilterFields = {
    businessId: selectedBusinessId || undefined,
    businessName: selectedBusinessId ? selectedBusinessName : undefined,
  }

  // 선택된 스냅샷 (조회 대상 시점). as_of는 X-As-Of 헤더로 전역 반영됨.
  const [selectedSnapshot, setSelectedSnapshot] = useState<SnapshotItemResponse | null>(null)

  // 조회 범위 (start/end) — 전체 탭 제외 3개 탭에서 사용. 기본값은 선택 스냅샷의 기간.
  const selectionMode = period === 'weekly' ? 'week' : 'month'
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<'period'>('period')
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null])
  const [startDate, endDate] = dateRange

  // 주간 ↔ 월간 전환 시 같은 인스턴스가 재사용되므로 스냅샷/범위 초기화 (SnapshotSelect가 새 목록에서 자동 선택)
  const [prevPeriod, setPrevPeriod] = useState<ReportPeriod>(period)
  if (prevPeriod !== period) {
    setPrevPeriod(period)
    setSelectedSnapshot(null)
    setDateRange([null, null])
    // 이전 주기의 as_of가 새 주기의 스냅샷 목록 조회(X-As-Of)에 새지 않도록 즉시 정리
    setCurrentAsOf(null)
  }

  // 스냅샷 선택 → as_of를 동기적으로 컨텍스트에 반영(자식 fetch 전에 헤더 보장) + 조회 범위 기본값 설정
  const handleSnapshotChange = (snapshot: SnapshotItemResponse) => {
    setCurrentAsOf(snapshot.as_of)
    setSelectedSnapshot(snapshot)
    setDateRange(getSnapshotRange(snapshot))
  }

  const handleDateRangeChange = (newStart: Date | null, newEnd: Date | null) => {
    if (newStart && newEnd) setDateRange([newStart, newEnd])
  }

  // 당월/익월(월간) · 금주/차주(주간) 진행 현황 섹션 — 기간 필터를 기준으로 도출한다.
  // 당월/금주 = 기간 필터 그대로, 익월/차주 = 기간 필터의 다음 달 / 다음 주(+7일)
  const autoPeriodSections = useMemo(() => {
    if (!startDate || !endDate) return []

    const fmt = (d: Date) => `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`
    const rangeText = (s: Date, e: Date) => `(${fmt(s)} ~ ${fmt(e)})`

    if (period === 'monthly') {
      const [nextStart, nextEnd] = getMonthRange(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 1))
      const currentPrefix = `당월(${startDate.getMonth() + 1}월)`
      const nextPrefix = `익월(${nextStart.getMonth() + 1}월)`
      return [
        { key: 'current', listPrefix: currentPrefix, listRangeText: rangeText(startDate, endDate), title: `${currentPrefix} 진행 현황 ${rangeText(startDate, endDate)}`, startDate, endDate },
        { key: 'next', listPrefix: nextPrefix, listRangeText: rangeText(nextStart, nextEnd), title: `${nextPrefix} 진행 현황 ${rangeText(nextStart, nextEnd)}`, startDate: nextStart, endDate: nextEnd },
      ]
    }

    // 주간 — 주차 번호는 주 피커(WeekPicker)와 동일한 getWeekNumber 기준
    const nextStart = new Date(startDate)
    nextStart.setDate(nextStart.getDate() + 7)
    const nextEnd = new Date(endDate)
    nextEnd.setDate(nextEnd.getDate() + 7)
    const currentPrefix = `금주(${getWeekNumber(startDate)}주)`
    const nextPrefix = `차주(${getWeekNumber(nextStart)}주)`
    return [
      { key: 'current', listPrefix: currentPrefix, listRangeText: rangeText(startDate, endDate), title: `${currentPrefix} 진행 현황 ${rangeText(startDate, endDate)}`, startDate, endDate },
      { key: 'next', listPrefix: nextPrefix, listRangeText: rangeText(nextStart, nextEnd), title: `${nextPrefix} 진행 현황 ${rangeText(nextStart, nextEnd)}`, startDate: nextStart, endDate: nextEnd },
    ]
  }, [startDate, endDate, period])

  // 페이지 떠날 때 as_of 컨텍스트 정리 (다른 화면으로 새지 않게)
  useEffect(() => () => setCurrentAsOf(null), [])

  // '누적 업무 진행 현황' 탭 요약 — 선택 스냅샷 기준 월별 요약 로드 (기간은 X-As-Of 헤더가 결정)
  useEffect(() => {
    if (!selectedWorkspace?.id || !selectedSnapshot) return
    dispatch(fetchTaskSummaryMonth({
      workspaceId: selectedWorkspace.id,
      businessId: selectedBusinessId || undefined,
    }))
    // selectedSnapshot.as_of / 사업 필터 변경 시 재조회 (기간은 헤더로 전달)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedWorkspace?.id, selectedSnapshot?.as_of, selectedBusinessId])

  // 워크스페이스 설정에서 세부실행과제 표시가 OFF 면 세부실행과제 카드·열·탭을 숨긴다
  const showTasks = useShowTasks()

  // 요약 카드 데이터 (수행업무 / 세부실행과제 / 예산 / 성과) — 누적 현황과 동일
  const summaryChartData = useMemo(() => {
    if (!taskSummaryMonth) return null
    return [
      {
        type: 'task_item' as SummaryListPanelType,
        title: '수행업무',
        value: taskSummaryMonth.task_item.total.count_percentage,
        total: taskSummaryMonth.task_item.total.total_count,
        completed: taskSummaryMonth.task_item.total.completed_count,
        color: designTokens.colors.secondary.dark,
        label: '진행율',
      },
      {
        type: 'task' as SummaryListPanelType,
        title: '세부실행과제',
        value: taskSummaryMonth.task.total.count_percentage,
        total: taskSummaryMonth.task.total.total_count,
        completed: taskSummaryMonth.task.total.completed_count,
        color: designTokens.colors.primary.dark,
        label: '진행율',
      },
      {
        type: 'budget' as SummaryListPanelType,
        title: '예산',
        value: taskSummaryMonth.task_budget.total.figure_percentage,
        total: taskSummaryMonth.task_budget.total.total_target_figure,
        completed: taskSummaryMonth.task_budget.total.total_done_figure,
        color: designTokens.colors.warning,
        label: '집행율',
        isAmount: true,
      },
      {
        type: 'performance' as SummaryListPanelType,
        title: '성과',
        value: taskSummaryMonth.task_performance.total.count_percentage,
        total: taskSummaryMonth.task_performance.total.total_count,
        completed: taskSummaryMonth.task_performance.total.completed_count,
        color: designTokens.colors.success,
        label: '달성율',
      },
    ].filter(item => showTasks || item.type !== 'task')
  }, [taskSummaryMonth, showTasks])

  const [downloadingFormat, setDownloadingFormat] = useState<ReportFormat | null>(null)

  const handleDownload = async (format: ReportFormat) => {
    if (!selectedWorkspace?.id || !selectedSnapshot) {
      showError('워크스페이스 또는 조회 기간(스냅샷)이 없어 다운로드할 수 없습니다.')
      return
    }
    setDownloadingFormat(format)
    try {
      // 탭 병합으로 단일 페이지가 되어, HWP는 전체(all) 리포트를 내려받는다.
      // 스냅샷 시점은 X-As-Of 헤더로, 조회 범위(start/end)와 사업 필터는 쿼리로 전달한다.
      // 백엔드 신규 양식이 당월/누적·장기 지연/미완료·익월 목록을 기간 기준으로 만들기 때문에
      // 기간을 보내지 않으면 "전체 기간" 보고서가 생성된다.
      await downloadPeriodReport({
        workspaceId: selectedWorkspace.id,
        period,
        tab: 'all',
        format,
        startDate: startDate ? formatDateForAPI(startDate) : undefined,
        endDate: endDate ? formatDateForAPI(endDate) : undefined,
        businessId: selectedBusinessId || undefined,
      })
    } catch (error) {
      handleApiError(error, showError, '보고서 다운로드에 실패했습니다.')
    } finally {
      setDownloadingFormat(null)
    }
  }

  const handleDownloadHwp = () => handleDownload('hwpx')

  return (
    <div>
      {/* 상단 필터 영역 — 문서 스크롤 기준 sticky. 고정 헤더(70px) 바로 아래에 붙어
          스크롤을 내려도 항상 보인다. 내부 스크롤 컨테이너 없음(문서 스크롤 하나만 존재). */}
      <div
        className="sticky top-[70px] z-20 px-6 py-3 flex items-center gap-x-3 gap-y-2 flex-wrap shadow-sm"
        style={{ backgroundColor: designTokens.colors.gray[0] }}
      >
        {/* Left: 스냅샷 필터 */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              fontWeight: 600,
              color: designTokens.colors.gray[6],
            }}
          >
            스냅샷
          </span>
          <SnapshotSelect
            period={period}
            value={selectedSnapshot?.as_of ?? null}
            onChange={handleSnapshotChange}
          />
        </div>

        {/* Right: 기간 선택 + 사업 필터 + HWP 다운로드 */}
        <div className="flex items-center gap-2 flex-shrink-0 ml-auto flex-wrap justify-end">
          <div className="flex-shrink-0">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onDateRangeChange={handleDateRangeChange}
              timePeriod={selectedTimePeriod}
              onTimePeriodChange={setSelectedTimePeriod}
              triggerMode="icon"
              showDateText={true}
              workspaceId={selectedWorkspace?.id}
              selectionMode={selectionMode}
              persistToStorage={false}
            />
          </div>
          <div className="w-[200px] flex-shrink-0">
            <BusinessSelect
              workspaceId={selectedWorkspace?.id || ''}
              selectedBusinessId={selectedBusinessId}
              selectedBusinessName={selectedBusinessName || '[전체] 사업 선택'}
              onSelect={handleBusinessSelect}
              placeholder="[전체] 사업 선택"
              showAllOption={true}
            />
          </div>
          <Button
            size="medium"
            color="dark"
            onClick={handleDownloadHwp}
            disabled={downloadingFormat !== null}
            className="h-10 px-[10px] flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap"
          >
            <FileText className="w-4 h-4" />
            {downloadingFormat === 'hwpx' ? '다운로드 중...' : 'HWP 다운로드'}
          </Button>
        </div>
      </div>

      {/* 콘텐츠 — 문서 스크롤에 자연스럽게 흐른다 (별도 내부 스크롤 없음) */}
      <div className="px-6 py-4">
      {/* 스냅샷 미선택 시 안내, 선택 시 섹션들 (as_of 변경 시 key로 재마운트하여 재조회) */}
      {!selectedSnapshot ? (
        <div className="flex items-center justify-center py-20">
          <span
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-medium'][0],
              color: designTokens.colors.gray[4],
            }}
          >
            조회할 기간을 선택해주세요.
          </span>
        </div>
      ) : (
        <div key={selectedSnapshot.as_of}>

      {/* 누적 업무 진행 현황 */}
      <section className="mb-10">
          <h2
            className="mb-4"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['title-small'][0],
              fontWeight: designTokens.typography.fontSize['title-small'][1].fontWeight,
              lineHeight: designTokens.typography.fontSize['title-small'][1].lineHeight,
              color: designTokens.colors.gray[6],
            }}
          >
            {sectionTitlePrefix} 누적 업무 진행 현황
          </h2>
          <div
            className="bg-white rounded-lg border p-4"
            style={{ borderColor: designTokens.colors.gray[1] }}
          >
            {isSummaryLoading ? (
              <div className="flex items-center justify-center h-48">
                <span style={{ color: designTokens.colors.gray[4] }}>
                  요약 정보를 불러오는 중...
                </span>
              </div>
            ) : summaryChartData ? (
              /* 반응형 그리드 — 좁은 화면에선 1~2열로 줄바꿈되어 radialBar 차트가
                 눌려 깨지지 않는다 (한 줄 flex 고정 시 카드 폭 < 차트 폭이 되는 문제 방지) */
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 items-stretch">
                {summaryChartData.map((item, index) => {
                  // 세부실행과제 카드가 빠질 수 있어 인덱스 대신 항목에 실린 목록 패널 타입을 쓴다
                  const cardType = item.type
                  return (
                  <div
                    key={index}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSummaryListPanel({ type: cardType, title: `${item.title} 목록`, ...businessFilterFields })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSummaryListPanel({ type: cardType, title: `${item.title} 목록`, ...businessFilterFields }) }}
                    className="flex flex-col items-center gap-2.5 rounded-lg p-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    title="클릭하면 전체 목록을 사이드패널로 봅니다"
                  >
                    {/* Title */}
                    <h3
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '20px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6],
                      }}
                    >
                      {item.title}
                    </h3>

                    {/* Radial Chart */}
                    <div className="relative">
                      <Chart
                        type="radialBar"
                        series={[item.value]}
                        height={170}
                        options={{
                          chart: {
                            type: 'radialBar',
                            sparkline: {
                              enabled: true,
                            },
                          },
                          plotOptions: {
                            radialBar: {
                              hollow: {
                                size: '55%',
                              },
                              track: {
                                background: designTokens.colors.gray[1],
                                strokeWidth: '100%',
                              },
                              dataLabels: {
                                show: true,
                                name: {
                                  show: true,
                                  fontSize: '14px',
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontWeight: 600,
                                  color: designTokens.colors.gray[4],
                                  offsetY: -10,
                                },
                                value: {
                                  show: true,
                                  fontSize: '18px',
                                  fontFamily: designTokens.typography.fontFamily.sans[0],
                                  fontWeight: 400,
                                  color: designTokens.colors.gray[6],
                                  offsetY: 5,
                                  formatter: function (val: number) {
                                    return val + '%'
                                  },
                                },
                              },
                            },
                          },
                          colors: [item.color],
                          labels: [item.label],
                          stroke: {
                            lineCap: 'round',
                          },
                        }}
                      />
                    </div>

                    {/* Count Text */}
                    <div
                      className="text-center"
                      style={{
                        fontFamily: designTokens.typography.fontFamily.sans[0],
                        fontSize: '16px',
                        fontWeight: 600,
                        color: designTokens.colors.gray[6],
                        lineHeight: 1.4,
                      }}
                    >
                      <div>
                        (전체) {item.isAmount
                          ? `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(item.total ?? 0)}원`
                          : `${(item.total ?? 0).toLocaleString()}개`}
                      </div>
                      <div>
                        (완료) {item.isAmount
                          ? `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 }).format(item.completed ?? 0)}원`
                          : `${(item.completed ?? 0).toLocaleString()}개`}
                      </div>
                    </div>
                  </div>
                  )
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-48">
                <span style={{ color: designTokens.colors.gray[4] }}>
                  요약 정보가 없습니다.
                </span>
              </div>
            )}
          </div>

          {/* 사업별 현황 / 부서별 현황 (상세 현황과 동일) */}
          <BusinessDepartmentSummary
            workspaceId={selectedWorkspace?.id}
            businessId={selectedBusinessId || undefined}
            titlePrefix={sectionTitlePrefix}
          />
      </section>

      {/* 당월/익월(월간) · 금주/차주(주간) 진행 현황 — 기간 필터 기준 자동 기간의 현황표 + 목록 */}
      {autoPeriodSections.map(section => (
        <section key={section.key} className="mb-10">
          <h2
            className="mb-4"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['title-small'][0],
              fontWeight: designTokens.typography.fontSize['title-small'][1].fontWeight,
              lineHeight: designTokens.typography.fontSize['title-small'][1].lineHeight,
              color: designTokens.colors.gray[6],
            }}
          >
            {sectionTitlePrefix} {section.title}
          </h2>
          <PeriodSummaryTable
            workspaceId={selectedWorkspace?.id}
            businessId={selectedBusinessId || undefined}
            startDate={section.startDate}
            endDate={section.endDate}
            onItemClick={(type, itemName) => setSummaryListPanel({
              type,
              title: `${itemName} 목록`,
              startDate: section.startDate,
              endDate: section.endDate,
              ...businessFilterFields,
            })}
          />

          {/* 상세 현황의 목록 UI (탭/필터/검색 + 테이블) */}
          <TaskListSection
            workspaceId={selectedWorkspace?.id}
            businessId={selectedBusinessId || undefined}
            startDate={section.startDate}
            endDate={section.endDate}
            titlePrefix={`${sectionTitlePrefix} ${section.listPrefix}`}
            titleSuffix={section.listRangeText}
          />
        </section>
      ))}

        </div>
      )}
      </div>

      {/* 요약 카드 클릭 → 항목별 목록 사이드패널 (+ 상세/보고서 패널) */}
      <SummaryListPanels
        panel={summaryListPanel}
        onClose={() => setSummaryListPanel(null)}
        workspaceId={selectedWorkspace?.id}
      />
    </div>
  )
}

export default PeriodReportDashboard
